import { useApolloClient, useMutation } from '@apollo/client/react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { refetchActiveApolloQueries } from '@/apollo/refetchActiveQueries'
import { authOidcDebugLog } from '@/auth/authOidcDebugLog'
import {
  shouldUseExchangeBackendRefresh,
  tryRenewExchangeSession,
} from '@/auth/keycloakExchangeSession'
import { getKeycloakUserManager } from '@/auth/keycloakUserManager'
import { useAuth } from '@/auth/useAuth'
import { useSwitchContextState } from '@/contexts/SwitchContextContext'
import {
  SwitchContextDocument,
  SwitchContextMutation,
  SwitchContextMutationVariables,
  SwitchContextUserInput,
} from '@/graphql/api/generated'
import { emitContextChanged } from '@/utils/contextChangedEvent'
import { markContextSwitchIntent } from '@/utils/contextSwitchIntent'

export interface SwitchContextResult {
  success: boolean
  contextIri: string | null
  contextName: string | null
}

export function useSwitchContext() {
  const { t } = useTranslation()
  const { syncOidcAccessToken } = useAuth()
  const client = useApolloClient()
  const [switchContextMutation, { loading, error }] = useMutation<
    SwitchContextMutation,
    SwitchContextMutationVariables
  >(SwitchContextDocument)
  const { isSwitching, setIsSwitching, isImpersonationExitOverlay } =
    useSwitchContextState()

  const switchContext = useCallback(
    async (
      contextIri: string | null,
      contextName?: string | null
    ): Promise<SwitchContextResult> => {
      const startedAt = Date.now()
      const currentContextIri =
        typeof window !== 'undefined'
          ? localStorage.getItem('contextIri')
          : null
      const nextContextIri = contextIri || null

      if (currentContextIri === nextContextIri) {
        authOidcDebugLog('context_switch_skip_same_context', {
          currentContextIri,
          nextContextIri,
          durationMs: Date.now() - startedAt,
        })
        return {
          success: true,
          contextIri: nextContextIri,
          contextName:
            contextName ||
            (typeof window !== 'undefined'
              ? localStorage.getItem('contextName')
              : null),
        }
      }

      const targetName = contextIri ? contextName || contextIri : null
      if (targetName) {
        localStorage.setItem('switchingTo', targetName)
      }
      markContextSwitchIntent()

      setIsSwitching(true)
      emitContextChanged('useSwitchContext:start')
      authOidcDebugLog('context_switch_start', {
        currentContextIri,
        nextContextIri,
      })

      try {
        const input: SwitchContextUserInput = {
          contextIri: contextIri || undefined,
        }

        const result = await switchContextMutation({
          variables: { input },
        })

        const output = result.data?.switchContextUser?.user

        if (output) {
          let nameToStore: string | null = null
          if (contextIri) {
            nameToStore =
              output.contextName ||
              contextName ||
              output.contextIri ||
              contextIri
          } else {
            nameToStore = output.contextName || null
          }

          if (output.requiresTokenRefresh) {
            const um = getKeycloakUserManager()
            try {
              const refreshStartedAt = Date.now()
              const sessionUser = await um.getUser()
              if (shouldUseExchangeBackendRefresh(sessionUser)) {
                await tryRenewExchangeSession(um)
              } else {
                await um.signinSilent()
              }
              await syncOidcAccessToken()
              authOidcDebugLog('context_switch_token_refresh_ok', {
                nextContextIri,
                durationMs: Date.now() - refreshStartedAt,
              })
            } catch {
              authOidcDebugLog('context_switch_token_refresh_failed', {
                nextContextIri,
              })
              // X-Communo-Context-Iri on GraphQL requests carries the selection; JWT refresh is best-effort only.
            }
          }

          if (contextIri) {
            localStorage.setItem('contextIri', contextIri)
          } else {
            localStorage.removeItem('contextIri')
          }

          if (nameToStore) {
            localStorage.setItem('contextName', nameToStore)
          } else {
            localStorage.removeItem('contextName')
          }

          if (contextIri) {
            localStorage.setItem('switchingTo', nameToStore || '')
          } else if (output.contextName) {
            localStorage.setItem('switchingTo', output.contextName)
          }

          emitContextChanged('useSwitchContext:success')
          // Clear cache then immediately refetch all active queries with the new context.
          await client.clearStore()
          refetchActiveApolloQueries(client)
          localStorage.removeItem('switchingTo')
          setIsSwitching(false)
          authOidcDebugLog('context_switch_success', {
            nextContextIri,
            durationMs: Date.now() - startedAt,
            requiresTokenRefresh: output.requiresTokenRefresh,
          })

          return {
            success: true,
            contextIri,
            contextName: nameToStore,
          }
        }

        setIsSwitching(false)
        return {
          success: false,
          contextIri: null,
          contextName: null,
        }
      } catch (err) {
        setIsSwitching(false)
        authOidcDebugLog('context_switch_failed', {
          nextContextIri,
          durationMs: Date.now() - startedAt,
        })
        console.error('Error switching context:', err)
        toast.error(t('context.switchError'))
        throw err
      }
    },
    [switchContextMutation, client, setIsSwitching, syncOidcAccessToken, t]
  )

  return {
    switchContext,
    loading,
    isSwitching,
    isImpersonationExitOverlay,
    error,
  }
}
